import React, { useMemo, useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import Link from 'shared/SlashLink';
import { scopeEstimate } from '../../data/scopeEstimate';
import { doorFeeIncome, doorFeeRpu } from '../../data/doorFee';
import PipelineReport from '../PipelineReport/PipelineReport';
import '../PipelineReport/PipelineReport.css';
import './PipelineExplorer.css';

const uniq = (arr) => [...new Set(arr)].filter(Boolean).sort();
const PAGE_SIZE = 12;

const fmtMoney = (n) => {
    if (!n) return null;
    if (n >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M';
    if (n >= 1e3) return '$' + Math.round(n / 1e3) + 'K';
    return '$' + n;
};

// Compact, windowed page numbers: 1 … 4 [5] 6 … 20
const pageWindow = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const out = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    if (start > 2) out.push('…');
    for (let i = start; i <= end; i++) out.push(i);
    if (end < total - 1) out.push('…');
    out.push(total);
    return out;
};

// Compact joined toggle bar — modern single-select control for short filter lists
// (housing type, credit type, sort). Keyboard operable, mirrors native <select> semantics
// (one active value, or '' for "all") without changing what gets stored in state.
const FilterSeg = ({ options, value, onChange, ariaLabel }) => (
    <div className="pipeline_seg" role="group" aria-label={ariaLabel}>
        {options.map((o) => (
            <button
                type="button"
                key={o.value}
                className="pipeline_segbtn"
                aria-pressed={value === o.value}
                onClick={() => onChange(o.value)}
            >
                {o.label}
            </button>
        ))}
    </div>
);

// Re-fits the map to the visible markers whenever the filtered set changes.
function FitBounds({ points }) {
    const map = useMap();
    React.useEffect(() => {
        if (points.length) {
            try {
                map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [40, 40], maxZoom: 14 });
            } catch (e) { /* noop */ }
        }
    }, [points, map]);
    return null;
}

/**
 * Interactive map + searchable / filterable project list.
 * Used by the pipeline hub (all projects) and per-county pages (pre-filtered).
 */
const PipelineExplorer = ({ projects, showCountyFilter = true, reportContext }) => {
    const [q, setQ] = useState('');
    const [county, setCounty] = useState('');
    const [htype, setHtype] = useState('');
    const [credit, setCredit] = useState('');
    const [year, setYear] = useState('');
    const [sort, setSort] = useState('recent');
    const [showReport, setShowReport] = useState(false);

    const counties = useMemo(() => uniq(projects.map((p) => p.county)), [projects]);
    const htypes = useMemo(() => uniq(projects.map((p) => p.housingType)), [projects]);
    const years = useMemo(() => uniq(projects.map((p) => p.year)).reverse(), [projects]);

    // CTCAC application numbers (p.id) repeat across multi-building applications,
    // so they are NOT unique. Derive a guaranteed-unique key for React lists —
    // duplicate keys otherwise break list reconciliation (stale/duplicate cards).
    const indexed = useMemo(() => projects.map((p, i) => ({ ...p, _uid: `${p.id || 'p'}-${i}` })), [projects]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return indexed.filter((p) => {
            if (county && p.county !== county) return false;
            if (htype && p.housingType !== htype) return false;
            if (credit && p.creditType !== credit) return false;
            if (year && p.year !== year) return false;
            if (needle && !`${p.name} ${p.developer} ${p.owner} ${p.city}`.toLowerCase().includes(needle)) return false;
            return true;
        });
    }, [indexed, q, county, htype, credit, year]);

    const mapped = filtered.filter((p) => p.lat && p.lng);
    const totalUnits = filtered.reduce((s, p) => s + (p.totalUnits || 0), 0);

    // Sort (markers/totals stay on `filtered`; only the card order changes).
    const sorted = useMemo(() => {
        const arr = [...filtered];
        if (sort === 'units') arr.sort((a, b) => (b.totalUnits || 0) - (a.totalUnits || 0));
        else if (sort === 'doorfee') arr.sort((a, b) => doorFeeIncome(b.totalUnits) - doorFeeIncome(a.totalUnits));
        else if (sort === 'name') arr.sort((a, b) => a.name.localeCompare(b.name));
        // 'recent' keeps the data's default order (award year desc).
        return arr;
    }, [filtered, sort]);

    // Pagination
    const [page, setPage] = useState(1);
    const listRef = useRef(null);
    const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageItems = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    // Reset to page 1 whenever the filter set or sort changes.
    useEffect(() => { setPage(1); }, [q, county, htype, credit, year, sort]);

    const goTo = (n) => {
        const next = Math.min(Math.max(1, n), totalPages);
        setPage(next);
        if (listRef.current) {
            const top = listRef.current.getBoundingClientRect().top + window.scrollY - 90;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    const reset = () => { setQ(''); setCounty(''); setHtype(''); setCredit(''); setYear(''); };

    return (
        <div className="pipeline_explorer">
            {/* Map */}
            <div className="pipeline_map mb-4" style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid #ead9cd' }}>
                <MapContainer center={[36.7, -119.4]} zoom={6} scrollWheelZoom={false} style={{ height: 460, width: '100%' }}>
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <FitBounds points={mapped} />
                    {mapped.map((p) => (
                        <CircleMarker
                            key={p._uid}
                            center={[p.lat, p.lng]}
                            radius={8}
                            pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#B85C38', fillOpacity: 0.9 }}
                        >
                            <Popup>
                                <strong>{p.name}</strong><br />
                                {p.developer}<br />
                                {p.city}, {p.county} County<br />
                                {p.creditType} credit &middot; {p.housingType}<br />
                                {p.totalUnits ? `${p.totalUnits} units` : ''} &middot; {p.year}
                            </Popup>
                        </CircleMarker>
                    ))}
                </MapContainer>
            </div>

            {/* Filters */}
            <div className="pipeline_filters row g-2 align-items-end mb-3">
                <div className="col-lg-3 col-md-6">
                    <label className="form-label small mb-1">Search</label>
                    <input className="form-control" type="search" placeholder="Builder, project, or city" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search projects" />
                </div>
                {showCountyFilter && (
                    <div className="col-lg-2 col-md-6">
                        <label className="form-label small mb-1">County</label>
                        <select className="pipeline_select" value={county} onChange={(e) => setCounty(e.target.value)} aria-label="Filter by county">
                            <option value="">All counties</option>
                            {counties.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                )}
                <div className="col-lg-2 col-md-6">
                    <label className="form-label small mb-1">Housing type</label>
                    <FilterSeg
                        ariaLabel="Filter by housing type"
                        value={htype}
                        onChange={setHtype}
                        options={[{ value: '', label: 'All types' }, ...htypes.map((c) => ({ value: c, label: c }))]}
                    />
                </div>
                <div className="col-lg-2 col-md-6">
                    <label className="form-label small mb-1">Credit</label>
                    <FilterSeg
                        ariaLabel="Filter by tax credit type"
                        value={credit}
                        onChange={setCredit}
                        options={[{ value: '', label: 'All' }, { value: '9%', label: '9%' }, { value: '4%', label: '4%' }]}
                    />
                </div>
                <div className="col-lg-2 col-md-6">
                    <label className="form-label small mb-1">Award year</label>
                    <select className="pipeline_select" value={year} onChange={(e) => setYear(e.target.value)} aria-label="Filter by award year">
                        <option value="">All years</option>
                        {years.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="col-lg-2 col-md-6">
                    <label className="form-label small mb-1">Sort by</label>
                    <FilterSeg
                        ariaLabel="Sort projects"
                        value={sort}
                        onChange={setSort}
                        options={[
                            { value: 'recent', label: 'Most recent' },
                            { value: 'units', label: 'Most units' },
                            { value: 'doorfee', label: 'Highest door-fee income' },
                            { value: 'name', label: 'Name (A–Z)' },
                        ]}
                    />
                </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" ref={listRef}>
                <p className="m-0 fw-semibold">
                    {filtered.length} project{filtered.length === 1 ? '' : 's'}
                    {filtered.length > PAGE_SIZE && (
                        <span className="text-muted fw-normal"> &middot; showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)}</span>
                    )}
                    {totalUnits ? <span className="text-muted fw-normal"> &middot; {totalUnits.toLocaleString()} total units</span> : null}
                </p>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    {filtered.length > 0 && (
                        <span className="roi-cta-note">
                            Generate your pipeline&rsquo;s potential ROI
                            <i className="fa-solid fa-arrow-right roi-cta-arrow" aria-hidden="true"></i>
                        </span>
                    )}
                    {filtered.length > 0 && (
                        <button type="button" className="roi-report-btn" onClick={() => setShowReport(true)}>
                            <i className="fa-solid fa-file-invoice-dollar" aria-hidden="true"></i> Generate ROI report
                        </button>
                    )}
                    {(q || county || htype || credit || year) && (
                        <button type="button" className="btn btn-outline-secondary" onClick={reset} style={{ padding: '11px 24px', fontWeight: 600 }}>Clear filters</button>
                    )}
                </div>
            </div>

            {/* List */}
            <div className="row">
                {pageItems.map((p) => (
                    <div className="col-xl-4 col-md-6 mb-4" key={p._uid}>
                        <div className="pipeline_card h-100" style={{ background: '#fff', border: '1px solid #ead9cd', borderRadius: 14, padding: 20 }}>
                            <div className="d-flex gap-2 mb-2 flex-wrap">
                                <span className="badge" style={{ background: '#B85C38' }}>{p.creditType}</span>
                                <span className="badge bg-secondary">{p.housingType}</span>
                                <span className="badge bg-light text-dark border">{p.year}</span>
                            </div>
                            <h3 className="h5 mb-1">{p.name}</h3>
                            <p className="text-muted small mb-2">{p.city}, {p.county} County</p>
                            <ul className="list-unstyled small m-0">
                                <li>
                                    <strong>Builder:</strong>{' '}
                                    {p.developer
                                        ? <Link to={`/housing-pipeline/developer/${p.developerSlug}`} title={`See all ${p.developer} projects`}>{p.developer}</Link>
                                        : '—'}
                                </li>
                                <li><strong>Units:</strong> {p.totalUnits ?? '—'}{p.lowIncomeUnits != null ? ` (${p.lowIncomeUnits} affordable)` : ''}</li>
                                <li><strong>Type:</strong> {p.construction}</li>
                                <li><strong>Status:</strong> {p.stage}</li>
                                {p.fedAnnualAward ? (
                                    <li><strong>Tax credit:</strong> {fmtMoney(p.fedAnnualAward)}/yr federal{p.stateAward ? ` + ${fmtMoney(p.stateAward)} state` : ''}</li>
                                ) : null}
                                <li className="text-muted">App #: {p.id}</li>
                            </ul>
                            {p.totalUnits ? (
                                <div className="mt-3 p-2 px-3" style={{ background: '#fbf3ee', border: '1px solid #f0dccd', borderRadius: 10 }}>
                                    <div className="small text-muted">Potential door-fee income (est.)</div>
                                    <div className="fw-bold" style={{ color: '#B85C38', fontSize: '1.15rem', lineHeight: 1.2 }}>
                                        ${doorFeeIncome(p.totalUnits).toLocaleString()}
                                    </div>
                                    <div className="small text-muted">${doorFeeRpu(p.totalUnits)}/unit one-time &middot; {p.totalUnits} units</div>
                                </div>
                            ) : null}
                            <div className="mt-3 pt-3" style={{ borderTop: '1px dashed #ead9cd' }}>
                                <div className="small fw-semibold mb-2" style={{ color: '#B85C38' }}>Likely BUILDLAB scope</div>
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                    {scopeEstimate(p).map((s) => (
                                        <span key={s} className="badge bg-light text-dark border" style={{ fontWeight: 500 }}>{s}</span>
                                    ))}
                                </div>
                                <Link to={p.totalUnits ? `/affordable-housing?units=${p.totalUnits}#roi` : '/affordable-housing#roi'} className="small fw-semibold" style={{ color: '#B85C38' }}>
                                    Estimate this project &rarr;
                                </Link>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
                <nav className="d-flex justify-content-center mt-2" aria-label="Project list pagination">
                    <ul className="pagination flex-wrap mb-0">
                        <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
                            <button type="button" className="page-link" onClick={() => goTo(safePage - 1)} aria-label="Previous page">&laquo;</button>
                        </li>
                        {pageWindow(safePage, totalPages).map((n, i) => (
                            n === '…'
                                ? <li className="page-item disabled" key={`e${i}`}><span className="page-link">…</span></li>
                                : (
                                    <li className={`page-item ${n === safePage ? 'active' : ''}`} key={n}>
                                        <button type="button" className="page-link" onClick={() => goTo(n)} aria-current={n === safePage ? 'page' : undefined}>{n}</button>
                                    </li>
                                )
                        ))}
                        <li className={`page-item ${safePage === totalPages ? 'disabled' : ''}`}>
                            <button type="button" className="page-link" onClick={() => goTo(safePage + 1)} aria-label="Next page">&raquo;</button>
                        </li>
                    </ul>
                </nav>
            )}
            {filtered.length === 0 && (
                <p className="text-center text-muted py-4">No projects match those filters.</p>
            )}

            {showReport && (
                <PipelineReport
                    projects={filtered}
                    contextLabel={reportContext || (county ? `${county} County` : 'your selected portfolio')}
                    onClose={() => setShowReport(false)}
                />
            )}
        </div>
    );
};

export default PipelineExplorer;
